/**
 * Data model for the per-kid "what to remember" School view.
 *
 * KidDayReminder records are one-per-child-per-date (never recurring timetable slots).
 * They are sourced from Compass sync, keyword rules, school emails (via Clerk), or
 * manual parent entry. The view reads and renders only — it does not parse emails.
 */

export type ReminderSection = 'wear' | 'bring' | 'today';
export type ReminderSource = 'compass' | 'email' | 'rule' | 'manual';

export interface KidDayReminder {
  id: string;
  memberId: string;
  date: string; // ISO 'YYYY-MM-DD' — one day, not recurring
  section: ReminderSection;
  emoji: string;
  label: string; // short, kid-readable, e.g. "Sport uniform"
  detail: string; // the why/when, e.g. "Sport — Period 2"
  time?: string; // for events, e.g. "8:30 AM"
  day?: string; // for week-ahead items, e.g. "Fri 19"
  source: ReminderSource;
  sourceRef?: string; // compassEventId | emailId — for traceability
  parentAction?: boolean; // needs a grown-up
  critical?: boolean; // hard not-to-miss → "Don't miss" badge
  missed?: boolean; // retrospective flag for the Today view
}

/**
 * Subjects TL;DR for a given date and member, sourced from Compass.
 * Keys: subjectsByDay[date][memberId]
 */
export type SubjectsByDay = Record<string, Record<string, string[]>>;

/**
 * Flagged subjects (tied to a reminder) to emphasise in the strip.
 * Keys: flaggedSubjects[date][memberId]
 */
export type FlaggedSubjects = Record<string, Record<string, string[]>>;

export const REMINDER_SECTIONS: Array<{
  key: ReminderSection;
  label: string;
  emoji: string;
  hint: string;
}> = [
  { key: 'wear', label: 'Wear', emoji: '👕', hint: 'Get dressed in' },
  { key: 'bring', label: 'Bring', emoji: '🎒', hint: 'Pack in the bag' },
  { key: 'today', label: 'Today', emoji: '📌', hint: "What's on" },
];

export interface SourceMeta {
  label: string;
  tone: string;
}

export const SOURCE_META: Record<ReminderSource, SourceMeta> = {
  compass: { label: 'Compass', tone: '#1184a7' },
  email: { label: 'Email', tone: '#fe7434' },
  rule: { label: 'Auto', tone: '#15a2a2' },
  manual: { label: 'Added', tone: '#94a7b5' },
};
